import { Severity } from "node-library/dist/constants";
import {
	FrontendCompanySampleMatrix,
	FrontendSampleSpecificationDirectory,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { ButtonTextLoadingAnimation } from "../animation/ButtonLoadingAnimation";
import { GreenButton } from "../button";
import { CustomTextInput } from "../inputs";
import { RedErrorIcon } from "../toast/errorIcons";
import { useSnackbar } from "../toast/showToastMessage";
import { ModalWithButtons } from "./modal";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;

	existingFolder: FrontendSampleSpecificationDirectory | undefined;

	testCategoryId: string;
	selectedMatrix: FrontendCompanySampleMatrix;
	parentFolder: FrontendSampleSpecificationDirectory | undefined;
	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;
}

export default function CreateSpecFolderModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		open,
		setOpen,
		testCategoryId,
		selectedMatrix,
		parentFolder,
		existingFolder,
		setSpecDirectories,
	} = props;

	const [folderName, setFolderName] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (open) {
			setFolderName(existingFolder ? existingFolder.name : "");
		}
	}, [open]);

	async function handleSubmit() {
		setLoading(true);
		try {
			if (!existingFolder) {
				const response = await sendPOSTRequest(
					"/specifications/create-specification-folder",
					{
						testCategoryId: testCategoryId,
						matrixId: selectedMatrix.matrixId,
						folderName,
						parentDirectoryId: parentFolder
							? parentFolder.directoryId
							: undefined,
					}
				);
				const newFolder: FrontendSampleSpecificationDirectory =
					response?.newDirectory;
				if (newFolder) {
					// Use functional update to append the new folder to the existing list
					setSpecDirectories((prev) => [...prev, newFolder]);
				}
			} else {
				const response = await sendPOSTRequest(
					"/specifications/rename-specification-folder",
					{
						specFolderId: existingFolder.directoryId,
						folderName,
					}
				);
				setSpecDirectories((prev) =>
					prev.map((dir) =>
						dir.directoryId === existingFolder.directoryId
							? { ...dir, name: folderName }
							: dir
					)
				);
			}
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const addSpecFolderModalContent: React.ReactNode = (
		<div className="registration-input-content">
			<CustomTextInput
				id={"spec-folder-name"}
				value={folderName}
				setValue={setFolderName}
				label={translation("FolderName")}
				type={"text"}
				required
			></CustomTextInput>
		</div>
	);
	const addSpecFolderModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				existingFolder ? translation("EditFolder") : translation("NewFolder")
			}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-create-folder-button"
					id="submit-create-folder-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Create")
						)
					}
					onClick={handleSubmit}
					disabled={loading}
				></GreenButton>,
			]}
		>
			{addSpecFolderModalContent}
		</ModalWithButtons>
	);
	return addSpecFolderModal;
}
